import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { http } from '@/api/http';
import { selectData, type SuccessResponseDTO } from '@/api/envelope';
import { formatDateTime } from '../lib/display';
import { SignalScoreBar } from './SignalScoreBar';

// --- API types for user moderation profile ---

interface CredibilityBreakdown {
  engagement: number;
  financial: number;
  maturity: number;
  track_record: number;
}

interface RecentAction {
  action: string;
  date: string;
  post_id: number | null;
  summary: string | null;
}

interface UserModerationData {
  risk_score: number;
  strike_count: number;
  max_strikes: number;
  prior_violations: number;
  credibility: CredibilityBreakdown | null;
  recent_actions: RecentAction[];
  account_flags: string[];
}

// --- API hook ---

const USER_MOD_PROFILE_KEY = (userId: number) => ['moderation', 'user-profile', userId] as const;

async function fetchUserModerationProfile(userId: number): Promise<UserModerationData> {
  const res = await http.get<SuccessResponseDTO<UserModerationData>>(
    `/admin/api/moderation/user/${userId}/profile`,
  );
  return selectData<UserModerationData>(res.data);
}

function useUserModerationProfile(userId: number) {
  return useQuery({
    queryKey: USER_MOD_PROFILE_KEY(userId),
    queryFn: () => fetchUserModerationProfile(userId),
    enabled: userId > 0,
  });
}

// --- Components ---

function riskColor(score: number): string {
  if (score < 0.2) return 'text-green-600';
  if (score < 0.5) return 'text-yellow-600';
  if (score < 0.8) return 'text-orange-600';
  return 'text-red-600';
}

function StrikeDots({ count, max }: { count: number; max: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: max }, (_, i) => (
        <span
          key={i}
          className={cn(
            'size-3 rounded-full border',
            i < count ? 'bg-red-500 border-red-600' : 'bg-muted border-muted-foreground/30',
          )}
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">
        {count}/{max}
      </span>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-40" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </CardContent>
    </Card>
  );
}

export interface UserModerationProfileProps {
  userId: number;
  role: 'creator' | 'reporter';
}

/**
 * User's moderation profile: risk gauge, strikes, violations,
 * credibility breakdown (if reporter), recent actions, and account flags.
 */
export function UserModerationProfile({ userId, role }: UserModerationProfileProps) {
  const { data, isLoading, isError } = useUserModerationProfile(userId);

  if (isLoading) return <ProfileSkeleton />;
  if (isError || !data) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Moderation Profile</CardTitle>
        <span className={cn('text-sm font-bold tabular-nums', riskColor(data.risk_score))}>
          {(data.risk_score * 100).toFixed(0)}%
        </span>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Risk Score Bar */}
        <SignalScoreBar label="Risk Score" score={data.risk_score} />

        {/* Strikes */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Strikes</span>
          <StrikeDots count={data.strike_count} max={data.max_strikes} />
        </div>

        {/* Prior Violations */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Prior Violations</span>
          <span className="text-xs font-medium">{data.prior_violations}</span>
        </div>

        {/* Credibility Breakdown (reporters) */}
        {role === 'reporter' && data.credibility && (
          <div className="space-y-2 border-t pt-2">
            <span className="text-xs font-medium text-muted-foreground">Credibility</span>
            <SignalScoreBar label="Engagement" score={data.credibility.engagement} />
            <SignalScoreBar label="Financial" score={data.credibility.financial} />
            <SignalScoreBar label="Maturity" score={data.credibility.maturity} />
            <SignalScoreBar label="Track Record" score={data.credibility.track_record} />
          </div>
        )}

        {/* Account Flags */}
        {data.account_flags.length > 0 && (
          <div className="flex flex-wrap gap-1 border-t pt-2">
            {data.account_flags.map((flag) => (
              <Badge key={flag} variant="outline" className="text-[10px]">
                {flag}
              </Badge>
            ))}
          </div>
        )}

        {/* Recent Actions */}
        {data.recent_actions.length > 0 && (
          <div className="space-y-1 border-t pt-2">
            <span className="text-xs font-medium text-muted-foreground">Recent Actions</span>
            {data.recent_actions.slice(0, 5).map((a, i) => (
              <div key={`${a.action}-${a.date}-${i}`} className="flex items-center justify-between text-xs">
                <span className="truncate text-muted-foreground">
                  {a.action}{a.summary ? ` — ${a.summary}` : ''}
                </span>
                <span className="shrink-0 text-muted-foreground">{formatDateTime(a.date)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
